/**
 * The contact read whole: the landscape window a double-click opens.
 *
 * The window is the shell and the verbs: a slim toolbar (Edit, Re-enrich, the
 * overflow menu, Close), the per-action result band, the merge picker and the
 * retire confirm. What the contact looks like is `ContactProfile`, which leads
 * with who they are and the one thing worth doing about them and keeps the
 * reference material in fixed places; on Edit the body swaps for the
 * full-field form in the same four-column geometry.
 *
 * Retire lives in the overflow menu behind an inline confirm and archives
 * the record: nothing is deleted anywhere in the CRM. Per-action results
 * (a save, a lookup, a restore) are shown in the band under the toolbar and
 * belong to THIS contact; the old page kept one string for the whole screen.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  Check,
  ChevronDown,
  GitMerge,
  ListPlus,
  Loader2,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  RotateCcw,
  UserMinus,
  X,
} from 'lucide-react'

import {
  crmErrorDetail,
  crmService,
  partyTypeFor,
  recordKindLabel,
  type CrmContact,
  type CrmContactPatch,
} from '@/services/crm'
import ContactEditForm from './ContactEditForm'
import DialogShell from './DialogShell'
import ContactProfile from './ContactProfile'
import { openDeal } from './contactLinks'
import { enrichResultNote, ignoredNote } from './crmFields'
import { HERO_ACTION } from './ProfileHero'
import { locationText } from './profileFacts'

export interface ContactWindowProps {
  /** The full service object — no page-local shape. */
  contact: CrmContact
  onClose: () => void
  /** Called after a successful retire so the page clears its selection. */
  onRetired?: (id: string) => void
  initialMode?: 'view' | 'edit'
  /** The page attaches its "Add to list" popover here; without it the menu
   *  item is not offered rather than offered inert. */
  onAddToList?: (id: string) => void
  /** Where a "Merge into" pick or an address clash's "Open existing" goes. */
  onOpenContact?: (id: string) => void
}

const CHIP = 'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px]'
const HEADER_BUTTON =
  'inline-flex items-center gap-1 rounded-md border border-amber-400/15 px-2.5 py-1 text-[11px] text-stone-300 hover:border-amber-400/30 hover:text-stone-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:opacity-50'
const MENU_ITEM =
  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] text-stone-300 hover:bg-amber-400/[0.06] hover:text-stone-100 focus-visible:bg-amber-400/[0.06] focus-visible:outline-none disabled:cursor-not-allowed disabled:text-stone-600'

/** A dropdown that closes on Escape without closing the window beneath it. */
function Dropdown({
  open,
  onClose,
  children,
  align = 'right',
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
  align?: 'left' | 'right'
}) {
  const ref = useRef<HTMLDivElement>(null)
  // A click anywhere else closes the menu, the way a menu is expected to go.
  useEffect(() => {
    if (!open) return
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.parentElement?.contains(event.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open, onClose])
  if (!open) return null
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onClose()
    }
  }
  return (
    <div
      ref={ref}
      role="menu"
      onKeyDown={onKeyDown}
      className={clsx(
        'absolute top-full z-10 mt-1 min-w-[200px] overflow-hidden rounded-md border border-amber-400/15 bg-[#0b0b0b] py-1 shadow-2xl',
        align === 'right' ? 'right-0' : 'left-0',
      )}
    >
      {children}
    </div>
  )
}

/**
 * Pick the record a captured contact folds into. Reads the directory the
 * page already holds under the same query key, so no second fetch.
 */
function MergePicker({
  contact,
  onPick,
  onCancel,
  pending,
}: {
  contact: CrmContact
  onPick: (targetId: string) => void
  onCancel: () => void
  pending: boolean
}) {
  const [needle, setNeedle] = useState('')
  const directory = useQuery({ queryKey: ['crm', 'contacts'], queryFn: () => crmService.getContacts() })
  const q = needle.trim().toLowerCase()
  const candidates = (directory.data?.contacts ?? [])
    .filter((c) => c.id !== contact.id && !c.retired)
    // The server folds a captured row onto a captured row, a broker master or
    // a sponsor master; a firm is not a person to fold into.
    .filter((c) => partyTypeFor(c.id) !== 'broker_firm')
    .filter((c) => {
      if (!q) return true
      return [c.displayName, c.company ?? '', c.email ?? '', ...(c.emails ?? [])].some((s) => s.toLowerCase().includes(q))
    })
    .slice(0, 8)
  return (
    <div className="rounded border border-amber-400/15 bg-black/30 px-3 py-2">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">
          Merge {contact.displayName} into…
        </span>
        <button type="button" onClick={onCancel} className={HEADER_BUTTON}>
          Cancel
        </button>
      </div>
      <input
        type="search"
        aria-label="Find the contact to merge into"
        placeholder="Name, company or address"
        value={needle}
        onChange={(event) => setNeedle(event.target.value)}
        className="input input-compact w-full"
        autoFocus
      />
      {directory.isPending ? (
        <div className="mt-1.5 text-[10px] text-stone-500">Reading the directory…</div>
      ) : candidates.length === 0 ? (
        <div className="mt-1.5 text-[10px] italic text-stone-500">No other contact matches.</div>
      ) : (
        <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto scrollbar-thin">
          {candidates.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                disabled={pending}
                onClick={() => onPick(c.id)}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-[11px] text-stone-200 hover:bg-amber-400/[0.06] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:opacity-50"
              >
                <span className="min-w-0 truncate">
                  {c.displayName}
                  {c.company ? <span className="ml-1.5 text-stone-500">· {c.company}</span> : null}
                </span>
                <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider text-stone-500">{recordKindLabel(c.id)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-1.5 text-[9px] text-stone-600">
        The deals, addresses and notes move to the record you pick; this one is archived pointing at it.
      </div>
    </div>
  )
}

export default function ContactWindow({ contact, onClose, onRetired, initialMode = 'view', onAddToList, onOpenContact }: ContactWindowProps) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode)
  const [note, setNote] = useState<{ text: string; tone: 'info' | 'warn' | 'error' } | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [dealsOpen, setDealsOpen] = useState(false)
  const [confirmRetire, setConfirmRetire] = useState(false)
  const [merging, setMerging] = useState(false)
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null)

  const name = contact.displayName
  const partyType = partyTypeFor(contact.id)
  const kindText = recordKindLabel(contact.id)
  const subtitle = [contact.title, contact.company, locationText(contact)].filter(Boolean).join(' · ')

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] })
    queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] })
    queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
  }

  const enrich = useMutation({
    mutationFn: () => crmService.enrichContact(contact.id),
    onSuccess: (result) => {
      setNote({ text: enrichResultNote(result), tone: 'info' })
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['crm', 'enrichment'] })
    },
    onError: (err) => setNote({ text: crmErrorDetail(err, 'The lookup could not be run.'), tone: 'error' }),
  })

  const retire = useMutation({
    mutationFn: () => crmService.archiveContact(contact.id),
    onSuccess: () => {
      invalidate()
      onRetired?.(contact.id)
      onClose()
    },
    onError: (err) => {
      setConfirmRetire(false)
      setNote({ text: crmErrorDetail(err, 'The contact could not be retired.'), tone: 'error' })
    },
  })

  const restore = useMutation({
    mutationFn: () => crmService.restoreContact(contact.id),
    onSuccess: () => {
      invalidate()
      setNote({ text: 'Restored to the directory.', tone: 'info' })
    },
    onError: (err) => setNote({ text: crmErrorDetail(err, 'The contact could not be restored.'), tone: 'error' }),
  })

  const confirm = useMutation({
    mutationFn: () => crmService.confirmContact(contact.id),
    onSuccess: () => {
      invalidate()
      setNote({ text: 'Confirmed.', tone: 'info' })
    },
    onError: (err) => setNote({ text: crmErrorDetail(err, 'The contact could not be confirmed.'), tone: 'error' }),
  })

  const merge = useMutation({
    mutationFn: (into: string) => crmService.mergeContact(contact.id, into),
    onSuccess: (_result, into) => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['crm', 'duplicates'] })
      setMerging(false)
      onClose()
      onOpenContact?.(into)
    },
    onError: (err) => setNote({ text: crmErrorDetail(err, 'The contacts could not be merged.'), tone: 'error' }),
  })

  const onSaved = (patch: CrmContactPatch, ignored: string[]) => {
    setMode('view')
    const kept = ignoredNote(ignored, kindText)
    if (kept) setNote({ text: kept, tone: 'warn' })
    else if (Object.keys(patch).length === 0) setNote({ text: 'Nothing changed.', tone: 'info' })
    else setNote({ text: 'Saved.', tone: 'info' })
  }

  const canMerge = partyType === 'deal_contact'
  const deals = contact.linkedDeals ?? []
  const propertyFor = (dealId: string) => contact.dealHistory?.find((d) => d.dealId === dealId)?.property ?? null

  // Chips that act on the record. They sit in the hero, beside the name, but
  // the mutations belong to the window so the result band can speak for them.
  const statusChips = (
    <>
      {contact.needsReview && !contact.retired ? (
        <span className={clsx(CHIP, 'border-amber-400/40 bg-amber-400/[0.08] text-amber-200')}>
          Needs review
          <button
            type="button"
            disabled={confirm.isPending}
            onClick={() => confirm.mutate()}
            className="inline-flex items-center gap-0.5 rounded border border-amber-400/40 px-1.5 text-[11px] text-amber-100 hover:bg-amber-400/[0.15] focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300/80 disabled:opacity-50"
          >
            <Check size={10} /> Confirm
          </button>
        </span>
      ) : null}
      {contact.retired ? (
        <span className={clsx(CHIP, 'border-red-400/40 bg-red-400/[0.06] text-red-300')}>
          Retired
          <button
            type="button"
            disabled={restore.isPending}
            onClick={() => restore.mutate()}
            className="inline-flex items-center gap-0.5 rounded border border-red-400/40 px-1.5 text-[11px] text-red-200 hover:bg-red-400/[0.15] focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300/80 disabled:opacity-50"
          >
            <RotateCcw size={10} /> Restore
          </button>
        </span>
      ) : null}
    </>
  )

  const dealsMenu =
    deals.length > 0 ? (
      <div className="relative">
        <button
          type="button"
          onClick={() => setDealsOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={dealsOpen}
          className={HERO_ACTION}
        >
          Open deal <ChevronDown size={13} />
        </button>
        <Dropdown open={dealsOpen} onClose={() => setDealsOpen(false)} align="left">
          {deals.map((id) => (
            <button
              key={id}
              type="button"
              role="menuitem"
              className={MENU_ITEM}
              onClick={() => {
                setDealsOpen(false)
                openDeal(id)
              }}
            >
              <span className="font-mono text-[10px] text-amber-300/80">{id}</span>
              <span className="min-w-0 truncate">{propertyFor(id) ?? <span className="italic text-stone-500">Property not on record</span>}</span>
            </button>
          ))}
        </Dropdown>
      </div>
    ) : null

  const header = (
    <div className="flex shrink-0 flex-col border-b border-amber-400/10">
      <div className="flex items-center gap-3 px-5 py-2.5">
        <div className="min-w-0 flex-1">
          {mode === 'edit' ? (
            // The hero (and its h2) belongs to reading; while editing, the
            // toolbar names the record being changed.
            <h2 className="truncate font-display text-[22px] font-semibold leading-tight text-stone-100">{name}</h2>
          ) : null}
          <div className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500">
            Contact · {kindText} · {contact.id}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {mode === 'edit' ? (
            // The form owns Save and Cancel (and their pending state); it
            // draws them here through this slot.
            <div ref={setActionsSlot} className="flex items-center" />
          ) : (
            <>
              <button type="button" onClick={() => setMode('edit')} className={HEADER_BUTTON}>
                <Pencil size={11} /> Edit
              </button>
              <button
                type="button"
                onClick={() => enrich.mutate()}
                disabled={enrich.isPending}
                title="Look this party up externally"
                className={HEADER_BUTTON}
              >
                <RefreshCw size={11} className={enrich.isPending ? 'motion-safe:animate-spin' : undefined} />
                {enrich.isPending ? 'Looking up…' : 'Re-enrich'}
              </button>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="More actions"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className={HEADER_BUTTON}
                >
                  <MoreHorizontal size={13} />
                </button>
                <Dropdown open={menuOpen} onClose={() => setMenuOpen(false)}>
                  {onAddToList ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={MENU_ITEM}
                      onClick={() => {
                        setMenuOpen(false)
                        onAddToList(contact.id)
                      }}
                    >
                      <ListPlus size={11} /> Add to list
                    </button>
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    className={MENU_ITEM}
                    disabled={!canMerge}
                    title={canMerge ? undefined : 'Only a captured contact can be merged into another record; masters merge from the broker directory.'}
                    onClick={() => {
                      setMenuOpen(false)
                      setConfirmRetire(false)
                      setMerging(true)
                    }}
                  >
                    <GitMerge size={11} /> Merge into…
                  </button>
                  {!contact.retired ? (
                    <button
                      type="button"
                      role="menuitem"
                      className={clsx(MENU_ITEM, 'text-red-300 hover:text-red-200')}
                      onClick={() => {
                        setMenuOpen(false)
                        setMerging(false)
                        setConfirmRetire(true)
                      }}
                    >
                      <UserMinus size={11} /> Retire
                    </button>
                  ) : null}
                </Dropdown>
              </div>
            </>
          )}
          <button type="button" onClick={onClose} aria-label="Close" className={clsx(HEADER_BUTTON, 'px-1.5')}>
            <X size={13} />
          </button>
        </div>
      </div>

      {note || confirmRetire || merging ? (
        <div className="space-y-2 px-5 pb-3">
          {note ? (
            <div
              role={note.tone === 'error' ? 'alert' : 'status'}
              className={clsx(
                'flex items-center justify-between gap-2 rounded border px-3 py-1.5 text-[11px]',
                note.tone === 'error' && 'border-red-400/30 bg-red-400/[0.06] text-red-300',
                note.tone === 'warn' && 'border-amber-400/30 bg-amber-400/[0.06] text-amber-200',
                note.tone === 'info' && 'border-amber-400/10 bg-black/30 text-stone-300',
              )}
            >
              <span>{note.text}</span>
              <button type="button" onClick={() => setNote(null)} aria-label="Dismiss" className="rounded text-stone-500 hover:text-stone-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60">
                <X size={11} />
              </button>
            </div>
          ) : null}
          {confirmRetire ? (
            <div className="flex flex-wrap items-center gap-2 rounded border border-red-400/30 bg-red-400/[0.06] px-3 py-1.5 text-[11px] text-red-200">
              <span className="min-w-0 flex-1">
                Retire {name}? The record is archived and leaves the directory; nothing is deleted, and capture will not write it back.
              </span>
              <button
                type="button"
                disabled={retire.isPending}
                onClick={() => retire.mutate()}
                className="inline-flex items-center gap-1 rounded-md border border-red-400/40 bg-red-400/[0.12] px-2 py-0.5 text-[11px] text-red-100 hover:bg-red-400/[0.2] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:opacity-50"
              >
                {retire.isPending ? <Loader2 size={10} className="motion-safe:animate-spin" /> : null}
                Yes, retire
              </button>
              <button type="button" onClick={() => setConfirmRetire(false)} className={HEADER_BUTTON}>
                Cancel
              </button>
            </div>
          ) : null}
          {merging ? <MergePicker contact={contact} pending={merge.isPending} onPick={(into) => merge.mutate(into)} onCancel={() => setMerging(false)} /> : null}
        </div>
      ) : null}
    </div>
  )

  return (
    <DialogShell label={`${name} — contact`} onClose={onClose} width="landscape" header={header} zIndex={80}>
      {mode === 'edit' ? (
        <ContactEditForm
          key={contact.id}
          contact={contact}
          layout="columns"
          actionsSlot={actionsSlot}
          onSaved={onSaved}
          onCancel={() => setMode('view')}
          onOpenContact={onOpenContact}
        />
      ) : (
        <ContactProfile
          key={contact.id}
          contact={contact}
          subtitle={subtitle}
          chips={statusChips}
          dealsMenu={dealsMenu}
          onReenrich={() => enrich.mutate()}
          reenrichPending={enrich.isPending}
          onOpenContact={onOpenContact}
          onNavigate={onClose}
        />
      )}
    </DialogShell>
  )
}
